<template>
	<div class="gastos_tab report_theme_container" data-theme="gastos">
		<TabHeader
			title="Gastos"
			description="Analiza la tendencia y composición de tus gastos"
			:filterPeriod="filterPeriod"
			:filterOptions="visiblePeriodOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			calendar-mode="range"
			:date-range="calendarRange"
			:year-range="yearRange"
			:table-title="chartsSectionTitle"
			table-title-collapsible
			:table-expanded="chartsExpanded"
			@toggle-table="toggleCharts"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange">
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button type="button"
						class="filter_btn"
						:class="{ 'is-active': isCuentasMode }"
						@click.stop="setSubFilter('cuentas')">
						Cuentas
					</button>
					<button v-for="category in categories"
						:key="category"
						type="button"
						class="filter_btn"
						:class="{ 'is-active': activeSubFilter === category }"
						:disabled="isCategoryDisabled(category)"
						@click.stop="setSubFilter(category)">
						{{ capitalize(category) }}
					</button>
					<!-- Tipos de contraparte: mismo estilo segmentado que el resto
						del control (como "Clientes" en Ventas), pero con su chevron
						de desplegable. Al presionarlo marca todos los tipos y abre
						el panel para deseleccionar. -->
					<MultiSelectDropdown
						segmented
						:value="selectedCounterpartTypes"
						:options="counterpartTypeOptions"
						placeholder="Contrapartes"
						select-all-on-open
						@input="SET_SELECTED_COUNTERPART_TYPES" />
				</div>
			</template>
			<!-- Filtro de items en la barra del header sticky → siempre visible
				al hacer scroll de la tabla. -->
			<template #table-bar-actions>
				<MultiSelectDropdown
					:value="selectedItems"
					:options="itemFilterOptions"
					:placeholder="itemFilterPlaceholder"
					:grouped="activeSubFilter !== 'contrapartes'"
					searchable
					align="right"
					@input="SET_SELECTED_ITEMS" />
			</template>
		</TabHeader>

		<!-- Charts Section: gráficos del header (stacked + líneas por
			contraparte/cuenta), homologado con Ventas. Colapsable, estado
			compartido por la storage-key de los gráficos. El toggle "% de ventas"
			se habilita en una fase posterior (cuando la tabla soporte el modo %). -->
		<!-- Cuerpo de los gráficos: headless (el toggle/título vive en el header
			sticky) y controlado por `chartsExpanded`. Queda abajo, NO sticky,
			scrollea con el contenido. -->
		<CollapsibleSection class="mt-1"
			headless
			:controlled-expanded="chartsExpanded">
			<SalesChartsRow
				:charts-data="headerChartsData"
				:is-loading="headerChartsLoading"
				:error="headerChartsError"
				:percent-mode.sync="percentMode"
				:sales-by-period="salesByPeriod"
				:total-label="chartTotalLabel" />
		</CollapsibleSection>

		<!-- Main Table Section -->
		<ExpensesEvolutionTable class="mt-2"
			slim-header
			:monthHeaders="monthHeaders"
			:visibleRows="visibleRows"
			:totalRow="totalRow"
			:ratioRow="ratioRow"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			:period-select-enabled="false"
			:percent-mode="percentMode"
			:sales-by-period="salesByPeriod"
			:clickable-cells="true"
			:cell-tooltips="true"
			:comparison-enabled="activeSubFilter !== 'contrapartes'"
			:comparison="comparisonViewModel"
			@toggle-l2="toggleL2"
			@cell-click="openExpensesAnalysis"
			@toggle-comparison="onToggleComparison"
			@comparison-add-year="onComparisonAddYear"
			@comparison-remove-year="onComparisonRemoveYear"
			@retry="fetchExpensesData" />
	</div>
</template>

<script>
import { createBreakdownTab } from "@/views/financialReports/utils/createBreakdownTab";
import HeaderExpensesAnalysis from "@/components/financialReports/expensesAnalysis/HeaderExpensesAnalysis.vue";
import BodyExpensesAnalysis from "@/components/financialReports/expensesAnalysis/BodyExpensesAnalysis.vue";
import { EXPENSE_COUNTERPART_TYPES } from "@/config/reportStructure";

// Toda la lógica (filtros, fetch, comparación, drill al modal de análisis,
// atajo al detalle de contraparte) vive en createBreakdownTab — Costos y Gastos
// la comparten. Acá sólo va la config propia del tab. Ver createBreakdownTab.js.
export default createBreakdownTab({
	name: "GastosTab",
	theme: "gastos",
	sectionNoun: "Gastos",
	level1Name: "Gastos",
	drillTab: "gastos",
	reportNs: "expensesReport",
	analysisNs: "expensesAnalysis",
	counterpartTypes: EXPENSE_COUNTERPART_TYPES,
	openAnalysisName: "openExpensesAnalysis",
	headerComp: HeaderExpensesAnalysis,
	bodyComp: BodyExpensesAnalysis,
	actions: {
		data: "fetchExpensesData",
		charts: "fetchExpensesCharts",
		comparison: "fetchExpensesComparison",
		analysis: "fetchExpensesAnalysis",
	},
});
</script>

<style scoped lang="scss">
.gastos_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// El header común vive en TabHeader.vue. Acá solo queda el SCSS del control
// segmentado del slot #center-sub-nav (Cuentas + un chip por categoría + el
// dropdown de tipos de contraparte, todos dentro de .sub_nav_filters).
// Replicamos .sub_nav_filters/.filter_btn localmente porque TabHeader es
// scoped y sus estilos no llegan al markup inyectado al slot.

.sub_nav_filters {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	padding: 0 2px;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: var(--report-surface); }

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}

	// Categoría sin items clasificados: no seleccionable. Atenuado + sin hover.
	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;

		&:hover {
			background: transparent;
		}
	}
}
</style>
